import { useEffect, useState } from 'react';
import {
  PRINCIPLES, COLORS, SPACING, BREAKPOINTS, TYPOGRAPHY, EDP_DS_ID, COMPONENT_CATEGORIES,
  type ThemeMode,
} from '../../designsystem/tokens';
import { getStoredTheme, setStoredTheme } from '../../designsystem/theme';
import {
  Button, Badge, StatusChip, Card, KpiCard, ConfidenceIndicator,
  AIRecommendationCard, Skeleton, KpiSkeleton, EmptyState,
} from '../../components/ui';

export function DesignSystemPage() {
  const [theme, setTheme] = useState<ThemeMode>(() => getStoredTheme());

  useEffect(() => {
    setStoredTheme(theme);
  }, [theme]);

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-ai-600">{EDP_DS_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise Design System</h1>
        <p className="mt-1 text-sm text-slate-500">
          Visual language, design tokens, component library, and AI design framework — single source of truth for every screen.
        </p>
      </div>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Theme</p>
            <p className="text-xs text-slate-500">Light · Dark · System (EDP-00002 §5)</p>
          </div>
          <div className="flex gap-2">
            {(['light', 'dark', 'system'] as ThemeMode[]).map((m) => (
              <Button key={m} variant={theme === m ? 'primary' : 'secondary'} size="sm" onClick={() => setTheme(m)}>
                {m}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      <section>
        <h2 className="text-lg font-semibold">Design Principles</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {PRINCIPLES.map((p) => (
            <Card key={p.id} padding="sm">
              <p className="font-medium">{p.label}</p>
              <p className="mt-1 text-xs text-slate-500">{p.description}</p>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Color System</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(COLORS).map(([family, shades]) => (
            <Card key={family} padding="sm">
              <p className="mb-2 text-sm font-medium capitalize">{family}</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(shades).map(([shade, hex]) => (
                  <div key={shade} className="text-center">
                    <div className="h-8 w-8 rounded-md border border-slate-200" style={{ backgroundColor: hex }} title={`${family}-${shade}`} />
                    <p className="mt-0.5 text-[10px] text-slate-400">{shade}</p>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Typography</h2>
        <Card className="mt-4 space-y-3">
          <p className={TYPOGRAPHY.display}>Display — Page titles</p>
          <p className={TYPOGRAPHY.h1}>H1 — Workspace titles</p>
          <p className={TYPOGRAPHY.h2}>H2 — Section headers</p>
          <p className={TYPOGRAPHY.body}>Body — Default content text at 14px</p>
          <p className={TYPOGRAPHY.label}>Label — Form labels</p>
          <p className={TYPOGRAPHY.caption}>Caption — Metadata and timestamps</p>
          <p className={TYPOGRAPHY.kpi}>$485.2K</p>
          <p className={TYPOGRAPHY.ai}>AI Response — Relaxed line height for copilot output</p>
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Primitive Components</h2>
        <Card className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button>Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="ai">AI Action</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge>Default</Badge>
            <Badge variant="success">Success</Badge>
            <Badge variant="warning">Warning</Badge>
            <Badge variant="error">Error</Badge>
            <Badge variant="ai">AI</Badge>
            <StatusChip status="enabled" />
            <StatusChip status="pending" />
            <StatusChip status="failed" />
          </div>
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Business &amp; AI Components</h2>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <KpiCard label="Revenue" value="$485.2K" trend="+12.4% vs prior" trendUp />
          <KpiSkeleton />
          <AIRecommendationCard
            title="Increase SP Brand Defense budget $150 → $180"
            description="ROAS 6.73, budget-constrained 94.9% utilization"
            confidence={0.91}
            expectedImpact="+$4,200/month sales"
            priorityRank={1}
            requiresApproval
            type="increase_budget"
          />
          <div className="flex items-center gap-4">
            <ConfidenceIndicator score={0.91} />
            <ConfidenceIndicator score={0.72} />
            <ConfidenceIndicator score={0.45} />
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">State Design</h2>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <Card>
            <p className="mb-2 text-sm font-medium">Loading</p>
            <Skeleton lines={3} />
          </Card>
          <EmptyState
            title="No campaigns yet"
            description="Connect Amazon Advertising to sync campaigns and unlock AI recommendations."
            actionLabel="Connect Amazon"
            onAction={() => {}}
          />
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Grid &amp; Breakpoints</h2>
        <Card className="mt-4">
          <div className="flex flex-wrap gap-4 text-sm">
            {Object.entries(BREAKPOINTS).map(([name, px]) => (
              <span key={name} className="rounded-lg bg-slate-100 px-3 py-1 dark:bg-slate-800">
                {name}: {px === 0 ? 'base' : `≥${px}px`}
              </span>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-500">Spacing scale (px): {SPACING.join(' · ')}</p>
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Component Categories</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {COMPONENT_CATEGORIES.map((c) => (
            <Badge key={c} variant="info">{c}</Badge>
          ))}
        </div>
        <p className="mt-4 text-sm text-slate-500">
          Full registry: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">pkg/designsystem/components.go</code>
          · Spec: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">docs/edp/EDP-00002_ENTERPRISE_DESIGN_SYSTEM.md</code>
        </p>
      </section>

      <section className="rounded-xl border border-ai-200 bg-ai-50 p-6 dark:border-ai-800 dark:bg-ai-950/30">
        <h2 className="text-lg font-semibold text-ai-900 dark:text-ai-100">Governance</h2>
        <p className="mt-2 text-sm text-ai-800 dark:text-ai-200">
          Every screen SHALL inherit appearance from EDP-00002. Use components from <code className="rounded bg-white/60 px-1">components/ui/</code> and tokens from <code className="rounded bg-white/60 px-1">designsystem/tokens.ts</code>. Custom UI bypassing the design system requires UX review (UXS-0120).
        </p>
      </section>
    </div>
  );
}
